import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../hooks/useAuthContext";
import axios from "axios";
import { CONTACT_BASE_URL } from "../services/contactService";
import Swal from "sweetalert2";
import Loading from "../utils/Loading";
import { format } from "date-fns";
import { formatPhoneNumber } from "../utils/formatPhoneNumber";

const ContactsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuthContext();

  const getAllContacts = async () => {
    try {
      const response = await axios.get(
        `${CONTACT_BASE_URL}/api/contacts/list`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setContacts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to fetch contacts. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllContacts();
  }, []);

  const deleteContact = async (e, _id) => {
    e.preventDefault();
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      backdrop: true,
      focusCancel: true,
    });

    if (confirmResult.isConfirmed) {
      try {
        await axios.delete(`${CONTACT_BASE_URL}/api/contacts/delete/${_id}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        Swal.fire({
          title: "Deleted!",
          text: "Contact has been removed.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        getAllContacts();
      } catch (error) {
        setError("Failed to delete contact. Please try again.");
      }
    }
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      `${contact.firstName} ${contact.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.subject &&
        contact.subject.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) return <Loading />;

  return (
    <div className="container-fluid py-4 px-lg-5">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
            <div>
              <h1 className="h2 fw-bold mb-0">
                <i className="fa fa-envelope-open-text me-2 text-primary"></i>
                Contact Messages
              </h1>
              <p className="text-muted mb-0">Manage all incoming messages</p>
            </div>
            <div className="bg-primary text-white p-3 rounded-3 text-center shadow-sm">
              <h2 className="mb-0 fw-bold">{contacts.length}</h2>
              <small className="text-white-50">TOTAL MESSAGES</small>
            </div>
          </div>
          <hr className="mt-3" />
        </div>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center">
          <i className="fa fa-exclamation-circle me-2"></i>
          <div>{error}</div>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-12 col-md-6 col-lg-4">
          <div className="input-group mb-3">
            <span className="input-group-text bg-white">
              <i className="fa fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredContacts.length > 0 ? (
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
          {filteredContacts.map((contact) => (
            <div className="col" key={contact._id}>
              <div className="card h-100 border-0 shadow-sm hover-shadow transition-all">
                <div className="card-header bg-primary bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-0 fw-semibold">
                      {contact.firstName} {contact.lastName}
                    </h5>
                    <small className="text-muted">
                      <i className="far fa-clock me-1"></i>
                      {format(
                        new Date(contact.createdAt),
                        "MMM d, yyyy - h:mm a"
                      )}
                    </small>
                  </div>
                  <span className="badge bg-primary rounded-pill">
                    <i className="fa fa-user me-1"></i>
                    {formatPhoneNumber(contact.phone) ||
                    formatPhoneNumber(contact.phone)
                      ? "Phone"
                      : "Email"}
                  </span>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fa fa-envelope text-muted me-2"></i>
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-decoration-none text-truncate"
                        title={contact.email}
                      >
                        {contact.email}
                      </a>
                    </div>
                    {formatPhoneNumber(contact.phone) && (
                      <div className="d-flex align-items-center mb-2">
                        <i className="fa fa-phone text-muted me-2"></i>
                        <span>{formatPhoneNumber(contact.phone)}</span>
                      </div>
                    )}
                    {contact.subject && (
                      <div className="d-flex align-items-start mb-2">
                        <i className="fa fa-tag text-muted me-2 mt-1"></i>
                        <span>{contact.subject}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-top pt-3">
                    <h6 className="fw-semibold mb-2">
                      <i className="fa fa-comment-dots text-muted me-2"></i>
                      Message
                    </h6>
                    <p className="card-text text-muted">
                      {contact.description.length > 150
                        ? `${contact.description.substring(0, 150)}...`
                        : contact.description}
                    </p>
                  </div>
                </div>
                {user.isAdmin && (
                  <div className="card-footer bg-transparent border-0 d-flex justify-content-end">
                    <button
                      onClick={(e) => deleteContact(e, contact._id)}
                      className="btn btn-sm btn-outline-danger"
                      title="Delete contact"
                    >
                      <i className="fa fa-trash-alt me-1"></i>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-5 my-5">
          <div className="py-5">
            <i className="fa fa-inbox fa-4x text-muted mb-4 opacity-50"></i>
            <h4 className="text-muted mb-3">
              {searchTerm
                ? "No matching contacts found"
                : "No contact messages yet"}
            </h4>
            {searchTerm ? (
              <button
                className="btn btn-outline-primary"
                onClick={() => setSearchTerm("")}
              >
                Clear search
              </button>
            ) : (
              <p className="text-muted">New messages will appear here</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsList;
